import type { QpsAnswer, QpsNote, QpsParseResult, QpsQuestionModel } from '@/types/qps';

type RawRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is RawRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const parseAnswer = (raw: unknown): QpsAnswer | null => {
  if (
    !isRecord(raw) ||
    !isNonEmptyString(raw.id) ||
    !isNonEmptyString(raw.value) ||
    !isNonEmptyString(raw.reponse) ||
    typeof raw.bonneReponse !== 'boolean'
  ) {
    return null;
  }
  return {
    id: raw.id,
    value: raw.value,
    label: raw.reponse,
    isCorrect: raw.bonneReponse,
  };
};

// Footnotes are display-only: an incomplete entry is dropped without
// invalidating the question (same rule as the back end).
const parseNotes = (raw: unknown): QpsNote[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (note): note is RawRecord =>
        isRecord(note) &&
        Number.isInteger(note.numero) &&
        typeof note.contenu === 'string' &&
        note.contenu.trim().length > 0
    )
    .map((note) => ({ number: note.numero as number, content: note.contenu as string }))
    .sort((a, b) => a.number - b.number);
};

const parseQuestion = (raw: unknown): QpsQuestionModel | null => {
  if (
    !isRecord(raw) ||
    !isNonEmptyString(raw.id) ||
    !isNonEmptyString(raw.question) ||
    !Array.isArray(raw.reponses) ||
    !isRecord(raw.qps)
  ) {
    return null;
  }
  const answers = raw.reponses.map(parseAnswer);
  if (answers.length < 2 || answers.includes(null) || !answers.some((answer) => answer?.isCorrect)) {
    return null;
  }
  return {
    id: raw.id,
    label: raw.question,
    type: typeof raw.type === 'string' ? raw.type : '',
    rubricCode: typeof raw.qps.rubrique === 'string' ? raw.qps.rubrique : '',
    notes: parseNotes(raw.renvois),
    answers: answers as QpsAnswer[],
  };
};

/**
 * Turns the untyped `questionnaire` field sent by the API into a typed QPS.
 *
 * `invalid` is kept apart from `none` on purpose: a malformed questionnaire
 * must block the arbitrage, never be skipped, or Spirica rejects the operation.
 * No logger here so the file runs as is on web and app: the caller logs `reason`.
 */
const parseQpsQuestionnaire = (raw: unknown): QpsParseResult => {
  if (raw === null || raw === undefined) {
    return { status: 'none' };
  }
  if (!Array.isArray(raw) || raw.length === 0) {
    return { status: 'invalid', reason: 'QPS questionnaire is not a non-empty array' };
  }

  const questions = raw.map(parseQuestion);
  if (questions.includes(null)) {
    return { status: 'invalid', reason: 'QPS questionnaire contains a malformed question' };
  }

  const validQuestions = questions as QpsQuestionModel[];
  const firstMeta = (raw[0] as RawRecord).qps as RawRecord;
  if (!isNonEmptyString(firstMeta.questionnaire) || !isNonEmptyString(firstMeta.version)) {
    return { status: 'invalid', reason: 'QPS questionnaire is missing its code or version' };
  }

  return {
    status: 'valid',
    questionnaire: {
      code: firstMeta.questionnaire,
      version: firstMeta.version,
      questions: validQuestions,
    },
  };
};

export { parseQpsQuestionnaire };
